import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CacheEntry, GlobalFunctions } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';

/**
 * interface IRuleEdit
 */
export interface IRuleEdit {

	/** RuleEditID */
	RuleEditID: number;

	/** RuleID */
	RuleID: number; 

	/** PreviousConditions */
	PreviousConditions: string;

	/** NewConditions */
	NewConditions: string;

	/** UserID */
	UserID: number;

	/** Timestamp */
	Timestamp: number;

	/** PreviousDataPoints */
	PreviousDataPoints: string;

	/** NewDataPoints */
	NewDataPoints: string;

	/** PreviousEnabled */
	PreviousEnabled: boolean;

	/** NewEnabled */
	NewEnabled: boolean;

	/** PreviousCMMS */
	PreviousCMMSEnabled: boolean;

	/** NewCMMS */
	NewCMMSEnabled: boolean;
}

/**
 * class RuleEdit
 */
export class RuleEdit implements IRuleEdit {

	/**
	 * RuleEdit
	 * @param RuleEditID RuleEditID 
	 * @param RuleID RuleID 
	 * @param PreviousConditions PreviousConditions 
	 * @param NewConditions NewConditions 
	 * @param UserID UserID 
	 * @param Timestamp Timestamp 
	 * @param PreviousDataPoints PreviousDataPoints 
	 * @param NewDataPoints NewDataPoints  
	 * @param PreviousEnabled PreviousEnabled 
	 * @param NewEnabled NewEnabled 
	 * @param PreviousCMMSEnabled PreviousCMMSEnabled 
	 * @param NewCMMSEnabled NewCMMSEnabled 
	 */
	constructor(
		public RuleEditID: number,
		public RuleID: number,
		public PreviousConditions: string,
		public NewConditions: string,
		public UserID: number,
		public Timestamp: number,
		public PreviousDataPoints: string,
		public NewDataPoints: string,
		public PreviousEnabled: boolean,
		public NewEnabled: boolean,
		public PreviousCMMSEnabled: boolean,
		public NewCMMSEnabled: boolean,
	) { }
}

/**
 * interface IRuleSubscriber
 */
export interface IRuleSubscriber {

	/** Rule ID */
	RuleID: number;

	/** Rule Subscriber ID */
	RuleSubscriberID: number;

	/** User ID */
	UserID: number;
}

/**
 * class RuleSubscriber
 */
export class RuleSubscriber implements IRuleSubscriber {

	/**
	 * RuleSubscriber
	 * @param RuleID
	 * @param RuleSubscriberID
	 * @param UserID
	 */
	constructor(
		public RuleID: number,
		public RuleSubscriberID: number,
		public UserID: number
	) { }

}

/**
 * interface IRuleDefinition
 */
export interface IRuleDefinition {

	/** Which condition of the rule this definition applies to */
	Condition: number;

	/** Datapoint ID */
	DatapointID: number;

	/** Limit */
	Limit: number;

	/** Operator */
	Operator: string;

	/** Rule Definition ID */
	RuleDefID?: number;

	/** Which rule this applies to */
	RuleID?: number;

	/** LowerLimit for between operator */
	LowerLimit?: number;
}

/**
 * class RuleDefinition
 */
export class RuleDefinition implements IRuleDefinition {

	/**
	 * RuleDefinition
	 * @param Condition
	 * @param DatapointID
	 * @param Limit
	 * @param Operator
	 * @param RuleDefID
	 * @param RuleID
	 * @param LowerLimit 
	 */
	constructor(
		public Condition: number,
		public DatapointID: number,
		public Limit: number,
		public Operator: string,
		public RuleDefID?: number,
		public RuleID?: number,
		public LowerLimit?: number
	) { }

}

/**
 * interface Rule
 */
export interface IRule {

	/** Rule ID */
	RuleID?: number;

	/** Asset ID */
	AssetID: number;

	/** Asset Name */
	AssetName?: string;

	/** Rule Description */
	RuleDescription: string;

	/** Rule Text */
	RuleText: string;

	/** Rule Message */
	RuleMessage?: string;

	/** Enabled */
	RuleEnabled: boolean;

	/** CMMS Enabled */
	CMMSEnabled: boolean;

	/** Trigger Time */
	TriggerTime: number;

	/** Active */
	Active: boolean;

	/** CreatedByUserID */
	CreatedByUserID?: number;

	/** Rule Definitions */
	RuleDef?: RuleDefinition[];

	/** Rule Subscribers */
	RuleSubscribers?: RuleSubscriber[];

	/** EventsInPast30Days */
	EventsInPast30Days?: number;

	/** LastTriggered */
	LastTriggered?: number;

	/** ReadableRule */
	ReadableRule?: string;

	/** CreatedAt */
	CreatedAt?: number;

	/** SeriesColor */
	SeriesColor?: string;
}

/**
 * class Rule
 */
export class Rule implements IRule {

	/**
	 * Rule
	 * @param AssetID AssetID 
	 * @param RuleDescription RuleDescription 
	 * @param RuleText RuleText 
	 * @param RuleEnabled RuleEnabled 
	 * @param CMMSEnabled CMMSEnabled
	 * @param TriggerTime TriggerTime 
	 * @param Active Active 
	 * @param CreatedAt Time Created
	 * @param ReadableRule ReadableRule 
	 * @param CreatedByUserID CreatedByUserID 
	 * @param AssetName AssetName 
	 * @param RuleMessage RuleMessage 
	 * @param RuleDef RuleDef 
	 * @param RuleID RuleID 
	 * @param RuleSubscribers RuleSubscribers  
	 * @param EventsInPast30Days EventsInLast30Days 
	 * @param LastTriggered LastTriggered 
	 * @param SeriesColor Series Colour
	 */
	constructor(

		public AssetID: number,
		public RuleDescription: string,
		public RuleText: string,
		public RuleEnabled: boolean,
		public CMMSEnabled: boolean,
		public TriggerTime: number,
		public Active: boolean,
		public CreatedAt?: number,
		public ReadableRule?: string,
		public CreatedByUserID?: number,
		public AssetName?: string,
		public RuleMessage?: string,
		public RuleDef?: RuleDefinition[],
		public RuleID?: number,
		public RuleSubscribers?: RuleSubscriber[],
		public EventsInPast30Days?: number,
		public LastTriggered?: number,
		public SeriesColor?: string
	) { }
}

/**
 * Injectable
 */
@Injectable({
	providedIn: 'root'
})
export class RulesDataService {

	/** apiUnits */
	private apiRules = "api/{0}/organisation/{1}/Rules";

	/** Local Cache placeholder */
	private APICache = new Map<string, CacheEntry<any>>();

	/**
	 * Default Constructor
	 * @param http Imports the 'HttpClient' Object for internet requests
	 * @param globalFunctions Global Functions
	 * @param siteSubscriptions site Subscriptions
	 */
	constructor(
		private http: HttpClient,
		public globalFunctions: GlobalFunctions,
		public siteSubscriptions: siteSubscriptions
	) { }

	/** clear Cache */
	clearCache<T>(
		cache: Map<string, CacheEntry<T>> = this.APICache): boolean {
		try {
			/** Clear the cache to re populate */
			cache.clear()
			return true;
		}
		catch { return false; }
	}

	/**
	 * Post a new rule
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param rule
	 * @returns
	 */
	postRule$(AccountGUID: string, orgUID: string, assetID: number, rule: Rule, userID: number
	): Observable<any> {
		let data = JSON.stringify(rule);

		let url = this.apiRules;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}`;

		url += `?userID=${userID}`;

		// Clear the cache
		this.clearCache()

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	 * Patch a rule
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param rule
	 * @returns
	 */
	patchRule$(AccountGUID: string, orgUID: string, assetID: number, rule: Rule, userID: number, deleted: boolean = false
	): Observable<any> {
		let data = JSON.stringify(rule);

		let url = this.apiRules;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}/rule`;
		url += `?ruleID=${rule.RuleID}`;
		url += `&userID=${userID}`;

		if (deleted) url += `&deleted=${deleted}`;

		/** Clear the cache to re populate */
		this.clearCache()

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	 * Get rules for an Asset Sensor and specific rule if requested
	 * @param AccountGUID 
	 * @param orgUID 
	 * @param assetID 
	 * @param ruleID 
	 * @returns 
	 */
	getRules$(AccountGUID: string, orgUID: string, assetID: number = 0, ruleID?: number
	): Observable<any> {

		let url = this.apiRules
			.replace("{0}", AccountGUID)
			.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${assetID}`;

		if (ruleID !== undefined) {
			url += `?ruleID=${ruleID}`;
		}
		return this.globalFunctions.cacheRequest(this.APICache, url, () => {
			return this.globalFunctions.fetchAPI(this.http, url, "GET", {});
		});
	}

	/**
	 * Get rules for a Webhook Sensor
	 * @param AccountGUID
	 * @param orgUID
	 * @param webhookSensorID
	 * @returns
	 */
	getRulesBySensorID$(AccountGUID: string, orgUID: string, webhookSensorID: number
	): Observable<any> {

		let data = {};

		let url = this.apiRules;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/sensor/${webhookSensorID}`;

		/** Check cache first for valid data */
		return this.globalFunctions.cacheRequest(this.APICache, url, () => {
			return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
		});
	}

	/**
	 * Get rules for asset
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param rule
	 * @returns
	 */
	deleteRule$(AccountGUID: string, orgUID: string, ruleID?: number
	): Observable<any> {
		let data = {};

		let url = this.apiRules;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/${ruleID}`;

		/** Clear the cache to re populate */
		this.clearCache();

		return this.globalFunctions.fetchAPI(this.http, url, "DELETE", data);
	}

	/**
	 * Get rules for an asset
	 * @param AccountGUID
	 * @param orgUID
	 * @param assetID
	 * @param ruleID
	 * @returns
	 */
	getRuleEdits$(AccountGUID: string, orgUID: string, ruleID: number
	): Observable<any> {
		let data = {};

		let url = this.apiRules;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/RuleEdits/${ruleID}`;

		/** Check cache first for valid data */
		return this.globalFunctions.cacheRequest(this.APICache, url, () => {
			return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
		});
	}

	/**
	 * postRuleReminder
	 * @param AccountGUID AccountGUID  
	 * @param orgUID orgUID 
	 * @param ruleID ruleID 
	 * @param delay delay 
	 * @returns 
	 */
	postRuleReminder$(AccountGUID: string, orgUID: string, ruleID: number, delay: number
	): Observable<any> {
		let data = {};

		let url = this.apiRules;
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", orgUID);

		url = `${environment.apiUrl}/${url}/Reminders/${ruleID}`;

		url += `?delay=${delay}`;
		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}
}
